import { productPriceInventory } from 'api/mdl22'
import { useDispatch, useSelector } from 'react-redux'
import {
  addBatchToCart,
  emptyCart,
  getPayloadProduct,
  isLoadingProduct,
} from 'redux/actions/cartActions'
import { addPriceList } from 'redux/actions/cotizacionActions'
import { isWebDocumentOrigin } from 'Utils/isWebDocumentOrigin'
import {
  fethCheckFinalPrice,
  processWarranties,
  buildFinalProductsInOrder,
} from './checkFinalPriceUtils'
import { getCartPriceChanges } from './getCartPriceChanges'

export const useCheckFinalPrice = () => {
  const dispatch = useDispatch()
  const company = useSelector(state => state.company)
  const quoteHeader = useSelector(state => state.cotizacion?.QuoteHeader)
  const cart = useSelector(state => state.cart.cart)

  const checkFinalPrice = async ({
    prods,
    customerId,
    newCompany,
    previousCompanyId,
    isClone,
    confirmRemoval,
  } = {}) => {
    // Callers that dispatch setCompany before calling must pass the company
    // they are leaving; otherwise the store value may already be the new one.
    const isCompanyChange =
      !!newCompany?.CompanyId &&
      newCompany.CompanyId !== (previousCompanyId ?? company.CompanyId)
    const isWebDocument = isWebDocumentOrigin(quoteHeader?.Origin)
    // A price list without a customer must not leak into another company,
    // including subsequent additions to an initially empty cart. Web documents
    // keep their own prices, so their price list is left untouched.
    const clearPreviousPriceList = () => {
      if (!customerId && isCompanyChange && !isWebDocument) {
        dispatch(addPriceList(undefined))
      }
    }
    dispatch(isLoadingProduct({ isLoadingAllProducts: true }))
    const allProducts = prods || cart
    if (!allProducts.length) {
      clearPreviousPriceList()
      if (prods) dispatch(emptyCart())
      return dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }

    // Separate warranty and regular products
    // Also detect warranty items by WarrantyProductId since backend may return IsExtendedWarranty: null
    const isWarrantyLine = p =>
      p.IsExtendedWarranty || p.WarrantyProductId != null
    const productList = allProducts.filter(p => !isWarrantyLine(p))
    const warrantyProducts = allProducts.filter(p => isWarrantyLine(p))

    const companyCode = newCompany?.CompanyCode || company.CompanyCode
    const companyId = newCompany?.CompanyId || company.CompanyId
    const WarehouseId = newCompany?.WarehouseId || company.WarehouseId

    const batches = []
    for (let i = 0; i < productList.length; i += 99) {
      batches.push(productList.slice(i, i + 99))
    }

    // Process warranties once — not once per batch
    const processedWarranties = processWarranties(warrantyProducts, newCompany)

    try {
      // Fetch all batches in parallel instead of sequentially
      const batchResults = await Promise.all(
        batches.map(async batch => {
          const ProductIds = batch.map(product => product.ProductId)
          const combinedPayload = batch.map(prod => ({
            product: prod,
            qty: prod.Quantity,
          }))

          const priceInventoryPayload = {
            CompanyId: companyId,
            CompanyCode: companyCode,
            WarehouseId,
            ProductIds,
            UoMId: null,
            PriceListId: null,
          }

          // Both API calls for this batch run in parallel
          const [response, processedProducts] = await Promise.all([
            productPriceInventory(priceInventoryPayload),
            fethCheckFinalPrice(
              combinedPayload,
              customerId,
              newCompany,
              isCompanyChange
            ),
          ])

          return {
            updatedProducts: response.data?.Data || [],
            processedProducts: processedProducts || [],
          }
        })
      )

      const allUpdatedProducts = batchResults.flatMap(r => r.updatedProducts)
      const allProcessedProducts = batchResults.flatMap(
        r => r.processedProducts
      )

      const orderedProducts = buildFinalProductsInOrder(
        allProducts,
        allProcessedProducts,
        processedWarranties
      )

      // Warranties use the effective sale price, including negotiated prices.
      const parentPriceMap = new Map()
      allProcessedProducts?.forEach(p => {
        const productId = p.selectList?.ProductId
        const product = p.payload?.product
        const price =
          !isClone && product?.NegotiatedReason ? product.Price : p.Price
        if (productId != null && price != null)
          parentPriceMap.set(productId, price)
      })

      const refreshBasePrice = !!newCompany && !isWebDocument

      orderedProducts?.forEach((orderedProduct, index) => {
        // Warranty prices are derived from their parent product below
        if (isWarrantyLine(orderedProduct.payload?.product ?? {})) return
        const updatedProduct = allUpdatedProducts?.find(
          up =>
            up.ProductId?.toUpperCase() ===
            orderedProduct.selectList.ProductId?.toUpperCase()
        )
        if (refreshBasePrice && updatedProduct?.BasePrice == null) {
          console.warn(
            'Missing BasePrice after company/branch change; retaining the previous base price.',
            {
              ProductId: orderedProduct.selectList.ProductId,
              CompanyId: companyId,
              WarehouseId,
            }
          )
        }
        if (updatedProduct) {
          orderedProducts[index].payload.product = {
            ...orderedProduct.payload.product,
            Cost: updatedProduct.Cost,
            AvgCost: updatedProduct.AvgCost,
            AvgPrice: updatedProduct.AvgPrice,
            ...(refreshBasePrice
              ? {
                  BasePrice:
                    updatedProduct.BasePrice ??
                    orderedProduct.payload.product.BasePrice,
                }
              : {}),
          }
        }
      })

      // Build all cart items and dispatch in a single state update
      const cartItems = orderedProducts.map(prod => {
        if (
          prod.payload?.product?.IsExtendedWarranty ||
          prod.payload?.product?.WarrantyProductId
        ) {
          const warrantyItem = prod.payload.product
          const parentPrice = parentPriceMap.get(warrantyItem.WarrantyProductId)
          let updatedWarrantyItem = {
            ...warrantyItem,
            IsExtendedWarranty: true,
          }

          if (parentPrice != null && warrantyItem.WarrantyPercent != null) {
            const newPrice = (parentPrice * warrantyItem.WarrantyPercent) / 100
            const qty = warrantyItem.Quantity || 1
            const taxRate = warrantyItem.TaxCategoryValue || 0
            const newAmount = newPrice * qty
            const newTax = newAmount * taxRate
            updatedWarrantyItem = {
              ...updatedWarrantyItem,
              Price: newPrice,
              BasePrice: newPrice,
              FinalPrice: newPrice,
              Amount: newAmount,
              AmountWithTax: newAmount + newTax,
              Tax: newTax,
            }
          }

          return {
            product: updatedWarrantyItem,
            qty: warrantyItem.Quantity || 1,
          }
        }
        return {
          product: getPayloadProduct(
            prod.selectList,
            prod.payload,
            prod.warehouse,
            prod.Quantity,
            prod.Price,
            isClone
          ),
          qty: prod.qty,
        }
      })

      const { retainedItems, removedProducts } = getCartPriceChanges(
        allProducts,
        cartItems
      )

      if (removedProducts.length && confirmRemoval) {
        const proceed = await confirmRemoval(removedProducts)
        if (!proceed) {
          return false
        }
      }

      clearPreviousPriceList()
      dispatch(emptyCart())
      dispatch(addBatchToCart(retainedItems))
      return true
    } catch (error) {
      // Lines passed in (editing or cloning a quote) must still reach the cart
      // when pricing fails; refreshing the current cart keeps it untouched.
      if (prods) {
        dispatch(emptyCart())
        dispatch(
          addBatchToCart(
            allProducts.map(product => ({ product, qty: product.Quantity }))
          )
        )
      }
      throw error
    } finally {
      // Always clear the loading flag regardless of success or failure
      dispatch(isLoadingProduct({ isLoadingAllProducts: false }))
    }
  }

  return { checkFinalPrice }
}
